import React, { useState } from 'react';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlaceNameAutocomplete from './PlaceNameAutocomplete';
import PlaceProvinceSelect from './PlaceProvinceSelect';
import {
  LEAD_PROFILE_CHANNEL_OPTIONS,
  LEAD_PROFILE_EMPLOYMENT_OPTIONS,
  LEAD_PROFILE_GENDER_OPTIONS,
  LEAD_PROFILE_HOUSING_OPTIONS,
  LEAD_PROFILE_MARITAL_OPTIONS,
  LEAD_PROFILE_PLACE_KIND_LABELS,
  LEAD_PROFILE_TIME_OPTIONS,
  describeLeadProfileChildAge,
  describeLeadProfilePlace,
  resizeChildrenYears,
} from '../leadProfile';
import {
  LEAD_PROFILE_CHILDREN_MAX,
  LEAD_PROFILE_MIN_BIRTH_DATE,
  LEAD_PROFILE_NAME_MAX,
  LEAD_PROFILE_OCCUPATION_MAX,
  computeLeadProfileAge,
  getLeadProfileTodayAR,
  suggestLeadProfileNames,
} from '../../services/leadsService';
import {
  LEADS_DANGER,
  LEADS_FORM_FIELD_GAP,
  LEADS_FORM_LABEL_GAP,
  LEADS_TEXT_MUTED,
  LEADS_TEXT_PRIMARY,
  getLeadStageFilterChipSx,
  leadsDateFieldSx,
  leadsGridChipSx,
  leadsSectionTitleSx,
  leadsSelectMenuProps,
  leadsTextFieldSx,
} from './leadsUi.styles';

// Chips de vivienda con texto corto (el resumen usa el largo).
const HOUSING_CHIP_OPTIONS = LEAD_PROFILE_HOUSING_OPTIONS.map((o) => ({
  ...o, label: o.value === 'first_home' ? 'Primera vivienda' : 'Ya es propietario/a',
}));

const twoColSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: LEADS_FORM_FIELD_GAP,
  alignItems: 'start',
};
const chipGridSx = (cols) => ({ display: 'grid', gap: 0.75, gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` });
const chipSx = (active) => ({ ...getLeadStageFilterChipSx(active), ...leadsGridChipSx, px: '6px' });
const groupLabelSx = { fontSize: '12px', color: 'rgba(251,251,251,0.72)', mb: LEADS_FORM_LABEL_GAP };
const errorTextSx = { fontSize: '12px', color: LEADS_DANGER, mt: '6px' };
const placeRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minHeight: 40,
  px: 1.25,
  borderRadius: '8px',
  border: '1px solid rgba(255,255,255,0.12)',
  bgcolor: 'rgba(255,255,255,0.03)',
};

function ProfileSelect({ id, label, value, options, onChange, error, disabled }) {
  return (
    <TextField
      id={id}
      select
      label={label}
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      error={Boolean(error)}
      helperText={error || undefined}
      SelectProps={{ MenuProps: leadsSelectMenuProps }}
      sx={leadsTextFieldSx}
    >
      <MenuItem value=""><em>Sin dato</em></MenuItem>
      {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
    </TextField>
  );
}

function ChipGroup({ id, label, options, value, multiple, onChange, error, disabled, cols }) {
  const selected = multiple ? value : (value ? [value] : []);
  const toggle = (opt) => {
    if (multiple) {
      const next = selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt];
      onChange(options.map((o) => o.value).filter((v) => next.includes(v)));
    } else onChange(selected.includes(opt) ? '' : opt);
  };
  return (
    <Box>
      <Typography id={id} sx={groupLabelSx}>{label}</Typography>
      <Box role="group" aria-labelledby={id} sx={chipGridSx(cols || options.length)}>
        {options.map((o) => (
          <Button
            key={o.value}
            size="small"
            disabled={disabled}
            aria-pressed={selected.includes(o.value)}
            onClick={() => toggle(o.value)}
            sx={chipSx(selected.includes(o.value))}
          >
            {o.label}
          </Button>
        ))}
      </Box>
      {error ? <Typography role="alert" sx={errorTextSx}>{error}</Typography> : null}
    </Box>
  );
}

/**
 * Campos editables de los datos del cliente (`profile`), dentro del form de
 * «Datos» de la ficha. Controlado: `form` (ver `leadProfileToForm`) +
 * `onChange(patch)`. `errors`: campo → mensaje (local o 400 `profile.<campo>`).
 *
 * @param {{ form: object, onChange: (patch: object) => void, errors?: Record<string,string>,
 *   disabled?: boolean, leadName?: string }} props
 */
export default function LeadProfileFields({ form, onChange, errors = {}, disabled = false, leadName = '' }) {
  const [province, setProvince] = useState(form.residence_location?.province || '');
  const today = getLeadProfileTodayAR();
  const age = computeLeadProfileAge(form.birth_date);
  const suggestion = !form.first_name && !form.last_name ? suggestLeadProfileNames(leadName) : null;
  const childrenYears = Array.isArray(form.children_birth_years) ? form.children_birth_years : [];
  const place = form.residence_location;

  // Un campo de año por hijo: al bajar la cantidad se recortan los sobrantes
  // (viajan en el mismo PATCH y evitan el 400 «Hay más años que hijos»); en 0 /
  // vacío se borran.
  const setCount = (raw) => {
    const value = raw.replace(/[^\d]/g, '').slice(0, 2);
    onChange({ children_count: value, children_birth_years: resizeChildrenYears(childrenYears, value) });
  };
  const setYear = (index, raw) => {
    const next = [...childrenYears];
    next[index] = raw.replace(/[^\d]/g, '').slice(0, 4);
    onChange({ children_birth_years: next });
  };
  const thisYear = Number(today.slice(0, 4));
  const yearInvalid = (y) => Boolean(y) && (y.length !== 4 || Number(y) < 1900 || Number(y) > thisYear);

  return (
    <Box data-testid="lead-profile-fields" sx={{ display: 'flex', flexDirection: 'column', gap: LEADS_FORM_FIELD_GAP }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 26 }}>
        <Typography sx={leadsSectionTitleSx}>Datos del cliente (opcional)</Typography>
        {suggestion ? (
          <Tooltip title={`Nombre «${suggestion.first_name}» y apellido «${suggestion.last_name}»`} arrow describeChild>
            <Button
              size="small"
              disabled={disabled}
              onClick={() => onChange(suggestion)}
              sx={{ textTransform: 'none', fontSize: '12px', color: LEADS_TEXT_MUTED, minWidth: 0, px: 0.75 }}
            >
              Completar desde el nombre
            </Button>
          </Tooltip>
        ) : null}
      </Box>

      <Box sx={twoColSx}>
        <TextField
          label="Nombre de pila"
          size="small"
          value={form.first_name}
          onChange={(e) => onChange({ first_name: e.target.value })}
          disabled={disabled}
          error={Boolean(errors.first_name)}
          helperText={errors.first_name || undefined}
          inputProps={{ maxLength: LEAD_PROFILE_NAME_MAX }}
          sx={leadsTextFieldSx}
        />
        <TextField
          label="Apellido"
          size="small"
          value={form.last_name}
          onChange={(e) => onChange({ last_name: e.target.value })}
          disabled={disabled}
          error={Boolean(errors.last_name)}
          helperText={errors.last_name || undefined}
          inputProps={{ maxLength: LEAD_PROFILE_NAME_MAX }}
          sx={leadsTextFieldSx}
        />
      </Box>

      <Box sx={twoColSx}>
        <TextField
          label="Fecha de nacimiento"
          type="date"
          size="small"
          value={form.birth_date}
          onChange={(e) => onChange({ birth_date: e.target.value })}
          disabled={disabled}
          error={Boolean(errors.birth_date)}
          helperText={errors.birth_date || (age != null ? `${age} ${age === 1 ? 'año' : 'años'}` : undefined)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: LEAD_PROFILE_MIN_BIRTH_DATE, max: today }}
          sx={leadsDateFieldSx}
        />
        <ProfileSelect
          id="lead-profile-gender"
          label="Género"
          value={form.gender}
          options={LEAD_PROFILE_GENDER_OPTIONS}
          onChange={(gender) => onChange({ gender })}
          error={errors.gender}
          disabled={disabled}
        />
      </Box>

      <Box sx={twoColSx}>
        <ProfileSelect
          id="lead-profile-marital"
          label="Estado civil"
          value={form.marital_status}
          options={LEAD_PROFILE_MARITAL_OPTIONS}
          onChange={(marital_status) => onChange({ marital_status })}
          error={errors.marital_status}
          disabled={disabled}
        />
        <ProfileSelect
          id="lead-profile-employment"
          label="Situación laboral"
          value={form.employment_status}
          options={LEAD_PROFILE_EMPLOYMENT_OPTIONS}
          onChange={(employment_status) => onChange({ employment_status })}
          error={errors.employment_status}
          disabled={disabled}
        />
      </Box>

      <TextField
        label="Ocupación / profesión"
        size="small"
        value={form.occupation}
        onChange={(e) => onChange({ occupation: e.target.value })}
        disabled={disabled}
        error={Boolean(errors.occupation)}
        helperText={errors.occupation || undefined}
        inputProps={{ maxLength: LEAD_PROFILE_OCCUPATION_MAX }}
        sx={leadsTextFieldSx}
      />

      <Box>
        <TextField
          label="Hijos"
          size="small"
          value={form.children_count}
          onChange={(e) => setCount(e.target.value)}
          disabled={disabled}
          error={Boolean(errors.children_count)}
          helperText={errors.children_count || undefined}
          inputProps={{ inputMode: 'numeric', maxLength: 2 }}
          placeholder={`0–${LEAD_PROFILE_CHILDREN_MAX}`}
          sx={{ ...leadsTextFieldSx, width: 120 }}
        />
        {childrenYears.length ? (
          <Box sx={{ mt: LEADS_FORM_FIELD_GAP }}>
            <Typography id="lead-profile-years-label" sx={groupLabelSx}>Año de nacimiento de cada hijo (opcional)</Typography>
            <Box
              role="group"
              aria-labelledby="lead-profile-years-label"
              data-testid="lead-profile-children-years"
              sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 1 }}
            >
              {childrenYears.map((year, index) => (
                <TextField
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  label={`Hijo ${index + 1}`}
                  size="small"
                  value={year}
                  onChange={(e) => setYear(index, e.target.value)}
                  disabled={disabled}
                  error={yearInvalid(year)}
                  helperText={describeLeadProfileChildAge(year) || ' '}
                  placeholder="Año"
                  inputProps={{ inputMode: 'numeric', maxLength: 4, 'aria-label': `Año de nacimiento del hijo ${index + 1}` }}
                  sx={leadsTextFieldSx}
                />
              ))}
            </Box>
            {errors.children_birth_years ? <Typography role="alert" sx={errorTextSx}>{errors.children_birth_years}</Typography> : null}
          </Box>
        ) : errors.children_birth_years ? (
          <Typography role="alert" sx={errorTextSx}>{errors.children_birth_years}</Typography>
        ) : null}
      </Box>

      <Box>
        <Typography sx={groupLabelSx}>Zona donde vive</Typography>
        {place ? (
          <Box sx={placeRowSx} data-testid="lead-profile-place">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '13px', color: LEADS_TEXT_PRIMARY }} noWrap>
                {describeLeadProfilePlace(place)}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: LEADS_TEXT_MUTED }}>
                {place.kind === 'other' ? 'Texto libre' : LEAD_PROFILE_PLACE_KIND_LABELS[place.kind]}
              </Typography>
            </Box>
            <Tooltip title="Quitar zona" arrow>
              <span>
                <IconButton
                  size="small"
                  aria-label="Quitar zona"
                  disabled={disabled}
                  onClick={() => onChange({ residence_location: null })}
                  sx={{ color: LEADS_TEXT_MUTED, '&:hover': { color: '#FFFFFF' } }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 2fr) minmax(0, 3fr)' }, gap: LEADS_FORM_FIELD_GAP }}>
            <PlaceProvinceSelect value={province} onChange={setProvince} disabled={disabled} label="Provincia" />
            <PlaceNameAutocomplete
              province={province}
              disabled={disabled}
              error={Boolean(errors.residence_location)}
              ariaLabel="Buscar zona donde vive"
              placeholder="Buscar barrio o ciudad"
              onPick={(picked) => onChange({ residence_location: picked })}
            />
          </Box>
        )}
        {errors.residence_location ? <Typography role="alert" sx={errorTextSx}>{errors.residence_location}</Typography> : null}
      </Box>

      <ChipGroup
        id="lead-profile-channels-label"
        label="Prefiere que lo contacten por"
        options={LEAD_PROFILE_CHANNEL_OPTIONS}
        value={form.preferred_contact_channels}
        multiple
        onChange={(preferred_contact_channels) => onChange({ preferred_contact_channels })}
        error={errors.preferred_contact_channels}
        disabled={disabled}
      />
      <ChipGroup
        id="lead-profile-times-label"
        label="Horario de contacto"
        options={LEAD_PROFILE_TIME_OPTIONS}
        value={form.preferred_contact_times}
        multiple
        onChange={(preferred_contact_times) => onChange({ preferred_contact_times })}
        error={errors.preferred_contact_times}
        disabled={disabled}
      />
      <ChipGroup
        id="lead-profile-housing-label"
        label="Vivienda"
        options={HOUSING_CHIP_OPTIONS}
        value={form.housing_status}
        onChange={(housing_status) => onChange({ housing_status })}
        error={errors.housing_status}
        disabled={disabled}
        cols={2}
      />
    </Box>
  );
}
